import React, { useEffect, useState } from "react";
import { Table } from "@emisgroup/ui-table";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { ProgressSpinner } from "@emisgroup/ui-progress-indicator";
import { TableDataRowProps, TableHeaderProps, TableProps } from "../../../Common/UI/Index";
import { NoDetails } from "../../../Common/NoDetails/NoDetails";
import { useInsights } from "../../../../service/hooks";
import { useSelections } from "../../../../hooks/useSelections";
import { TableDataProps } from "../../../Common/UI/Table/TableContainer/Types";
import { DataEmpty } from "../../Execution/ExecutionDetail/components/DataEmpty/DataEmpty";
import { DecorativeEmpty } from "./DecorativeEmpty";
import { IInsightsResults, IInsightValue, INCLUSION } from "./Types";

interface InsightTableProps {
  showInsights: boolean;
  filterCriteria: string;
  inclusion?: string;
  executionType: string;
  setShowInsights: (show: boolean) => void;
}

export const InsightTable: React.FC<InsightTableProps> = (
  { showInsights, filterCriteria, inclusion, executionType, setShowInsights }) => {
  const { t: translate } = useTranslation();
  const [insights, setInsights] = useState<IInsightsResults[]>([]);
  const [insightsData, setInsightsData] = useState<TableProps>({ headerRow: [], dataRow: [] } as TableProps);
  const executionId = sessionStorage.getItem("selectedExecutionId") as string;
  const [selectedRows] = useSelections();
  const insightRequest = useInsights({
    onSuccess: (data: { data: IInsightsResults[] }) => {
      const insightData: IInsightsResults[] = data.data;
      if (insightData) {
        setInsights(insightData);
      }
    }
  });
  const entity = executionType === "SITES" ? translate("Insights.Sites") : translate("Insights.Practices");

  const populateTableHeader = (titles: string[]): TableHeaderProps[] => {
    return [
      {
        title: titles[0],
        propertyName: "name",
        width: "50%",
        sortable: false
      },
      {
        title: titles[1],
        propertyName: "count",
        width: "25%",
        sortable: false
      },
      {
        title: titles[2],
        propertyName: "total",
        width: "25%",
        sortable: false
      }
    ];
  };

  const populateTableData = (insights: IInsightValue[]): TableDataRowProps[] => {
    return insights.map((insight: IInsightValue) => {
      const executionRow: TableDataRowProps = {
        row: [
          {
            value: insight.name,
            propertyName: "name",
          },
          {
            value: insight.count,
            propertyName: "count"
          },
          {
            value: insight.total,
            propertyName: "total"
          },
        ]
      };
      return executionRow;
    });
  };

  useEffect(() => {
    if (insights.length > 0) {
      const selectedInsightData = insights.find((insight: IInsightsResults) => insight.name === filterCriteria);
      if (selectedInsightData) {
        const table: TableProps = {
          headerRow: populateTableHeader(selectedInsightData.columnNames),
          dataRow: populateTableData(selectedInsightData.values)
        };
        setInsightsData(table);
      }
    }
  }, [filterCriteria, insights]);

  useEffect(() => {
    setShowInsights(selectedRows.length > 0);
    if (selectedRows.length > 0 && executionId) {
      insightRequest.mutate(
        { executionId: executionId, included: (inclusion === INCLUSION.INC), ids: selectedRows, type: executionType }
      );
    }
  }, [selectedRows, inclusion, executionId]);

  if(!showInsights) {
    return (
      <NoDetails
        style={{ paddingTop: "20vh" }}
        title={translate("Insights.NoSitesSelected", { entity })}
        description={translate("Insights.PleaseSelectSites", { entity: entity.toLowerCase() })}
        notFoundIcon={<DecorativeEmpty/>}
      />
    );
  }

  if(insightRequest.isPending) {
    return (
      <div style={{ position: "relative", height: "300px" }}>
        <ProgressSpinner delay={100} text={translate("Insights.LoadingMessage")}/>
      </div>
    );
  }

  if(insightsData.dataRow.length === 0) {
    return (
      <div style={{ paddingTop: "18vh" }}>
        <DataEmpty
          title={translate("NoDataAvailable")}
          description={translate("NoDataAvailableDescription")}
          icon={<DecorativeEmpty/>}
        />
      </div>
    );
  }

  return (
    <div className="insights-table">
      <Table data-testid="insightsTable" id="insightsTable">
        <thead className="table-thead">
          <tr>
            {insightsData.headerRow.map((header: TableHeaderProps) => (
              <th key={header.propertyName} style={{ width: header.width }}>
                {header.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {insightsData.dataRow.map((row: TableDataRowProps, index: number) => (
            <tr key={index}>
              {row.row.map((cell: TableDataProps, cellIndex: number) => (
                <td key={cellIndex}>{cell.value}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};
